import lottie from 'lottie-web';
import { preloadImages } from '@data/preload-assets';
import { loadFonts } from '@loaders/FontLoader';
import { loadSoundEffects } from '@utils/SoundFX';

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({
      key: 'LoadScene',
    });
  }

  async preload() {
    this.load.image('overlay', 'assets/GameAssets/overlay.png');

    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');

    await loadFonts();
  }

  create() {
    this.screenOverlay = this.add.image(0, 0, 'overlay').setOrigin(0);

    this.loadingAnim = this.addLoaderLottie(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
    );
    preloadImages(this);
    this.assetsLoad();

    this.loadVoices(0);

    loadSoundEffects(this);

    this.load.start();

    this.load.on(
      'progress',
      function () {
        this.loadingAnim.anim.playSegments([0, 25], true);
      },
      this,
    );

    this.load.on(
      'complete',
      function () {
        console.info('complete');
        this.loadingAnim.anim.loop = false;

        this.loadingAnim.anim.playSegments([26, 41], true);

        this.loadingAnim.anim.addEventListener('complete', () => {
          this.scene.start('StartScene');
        });
      },
      this,
    );
  }

  addLoaderLottie(_x, _y) {
    const lottieRef = this.add
      .dom(_x - this.cameras.main.width / 2, _y - this.cameras.main.height / 2, 'div')
      .setScale(0.7);

    const lottieDiv = lottieRef.node;

    lottieDiv.style.height = '100%';
    lottieDiv.style.width = '100%';

    const lottieAnim = lottie.loadAnimation({
      container: lottieDiv,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    const dataset = { anim: lottieAnim, dom: lottieRef };

    return dataset;
  }

  assetsLoad() {
    // Confetti animation atlas
    this.load.atlas(
      'confetti',
      'assets/GameAssets/checkConfettiAnim.png',
      'assets/GameAssets/checkConfettiAnim.json',
    );

    // The heavy combined city-building completion animation
    // (citybuilding/cityBuildingAnim) is lazy-loaded in GameScene on completion,
    // so it is intentionally not loaded here.
  }

  loadVoices(rangesOrMaxVo = 0, voiceKey = 'voice') {
    const ranges = [];

    if (Array.isArray(rangesOrMaxVo)) {
      if (rangesOrMaxVo.length % 2 !== 0) {
        console.error('Invalid range array: must contain an even number of elements.');
        return;
      }
      // Group the array into ranges [start1, end1, start2, end2, ...]
      for (let i = 0; i < rangesOrMaxVo.length; i += 2) {
        ranges.push([rangesOrMaxVo[i], rangesOrMaxVo[i + 1]]);
      }
    } else {
      // Treat as single number, default range [1, rangesOrMaxVo]
      ranges.push([1, rangesOrMaxVo]);
    }

    // Iterate over all ranges and load voices
    ranges.forEach(([start, end]) => {
      for (let i = start; i <= end; i++) {
        this.load.audio(`voice_${i}`, `assets/GameAssets/audio/${voiceKey}_${i}.mp3`);
        // console.log(`voice_${i}`);
      }
    });
  }

  loadVoicesIncludingSubfolders(
    rangesOrMaxVo = 0,
    subfoldername = '',
    voiceKey = 'voice',
  ) {
    const ranges = [];

    // Check if the input rangeOrMaxVo is an array and handle it
    if (Array.isArray(rangesOrMaxVo)) {
      if (rangesOrMaxVo.length % 2 !== 0) {
        console.error('Invalid range array: must contain an even number of elements.');
        return;
      }
      for (let i = 0; i < rangesOrMaxVo.length; i += 2) {
        ranges.push([rangesOrMaxVo[i], rangesOrMaxVo[i + 1]]);
      }
    } else {
      // Treat as a single number (range [1, rangesOrMaxVo])
      ranges.push([1, rangesOrMaxVo]);
    }

    const basePath = 'assets/GameAssets/audio';
    const subfolderPath = subfoldername ? `${basePath}/${subfoldername}` : basePath;

    // Iterate over the ranges and load the voices
    // Try loading from subfolder first if specified, otherwise from base folder
    ranges.forEach(([start, end]) => {
      for (let i = start; i <= end; i++) {
        const key = `${voiceKey}_${i}`;
        const audioPath = subfoldername
          ? `${subfolderPath}/${key}.mp3`
          : `${basePath}/${key}.mp3`;

        this.load.audio(key, audioPath);
      }
    });

    // Log that the loading process has finished
    console.info('Voice loading completed.');
  }
}
